import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  Joyride,
  ACTIONS,
  EVENTS,
  STATUS,
  type EventData,
  type Controls,
  type Step,
} from 'react-joyride'
import { useDemoTour } from '@/features/demo/tour/useDemoTour'
import { useMicroTour } from '@/features/assistant/tours/MicroTourProvider'
import PageTourTooltip from './PageTourTooltip'

function waitForElement(
  selector: string,
  timeout = 3000,
): Promise<Element | null> {
  return new Promise((resolve) => {
    const existing = document.querySelector(selector)
    if (existing) {
      resolve(existing)
      return
    }
    const start = Date.now()
    const interval = setInterval(() => {
      const el = document.querySelector(selector)
      if (el) {
        clearInterval(interval)
        resolve(el)
      } else if (Date.now() - start > timeout) {
        clearInterval(interval)
        resolve(null)
      }
    }, 100)
  })
}

export interface PageTourContextValue {
  startTour: (pageKey: string, steps: Step[]) => void
  stopTour: () => void
  isActive: boolean
  activePageKey: string | null
}

const PageTourContext = createContext<PageTourContextValue>({
  startTour: () => {},
  stopTour: () => {},
  isActive: false,
  activePageKey: null,
})

export function usePageTourContext() {
  return useContext(PageTourContext)
}

export default function PageTourProvider({
  children,
}: {
  children: ReactNode
}) {
  const demoTour = useDemoTour()
  const microTour = useMicroTour()

  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [steps, setSteps] = useState<Step[]>([])
  const [activePageKey, setActivePageKey] = useState<string | null>(null)
  const stepsRef = useRef<Step[]>([])

  const startTour = useCallback(
    (pageKey: string, nextSteps: Step[]) => {
      if (demoTour.isActive || microTour.isActive) return
      if (!nextSteps.length) return

      setSteps(nextSteps)
      stepsRef.current = nextSteps
      setActivePageKey(pageKey)
      setStepIndex(0)

      const firstTarget = nextSteps[0].target
      if (typeof firstTarget !== 'string') {
        setRun(true)
        return
      }
      waitForElement(firstTarget, 3000).then((el) => {
        if (el) setRun(true)
      })
    },
    [demoTour.isActive, microTour.isActive],
  )

  const stopTour = useCallback(() => {
    setRun(false)
    setSteps([])
    stepsRef.current = []
    setStepIndex(0)
    setActivePageKey(null)
  }, [])

  const handleEvent = useCallback((data: EventData, _controls: Controls) => {
    const { action, index, type, status } = data

    if (
      status === STATUS.FINISHED ||
      status === STATUS.SKIPPED ||
      action === ACTIONS.CLOSE
    ) {
      setRun(false)
      setActivePageKey(null)
      return
    }

    if (type === EVENTS.STEP_AFTER) {
      const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1
      if (nextIndex < 0) return
      if (nextIndex >= stepsRef.current.length) {
        setRun(false)
        setActivePageKey(null)
        return
      }
      setStepIndex(nextIndex)
    }
  }, [])

  const value = useMemo(
    () => ({ startTour, stopTour, isActive: run, activePageKey }),
    [startTour, stopTour, run, activePageKey],
  )

  return (
    <PageTourContext.Provider value={value}>
      {children}
      {steps.length > 0 && (
        <Joyride
          steps={steps}
          run={run}
          stepIndex={stepIndex}
          continuous
          onEvent={handleEvent}
          tooltipComponent={PageTourTooltip}
          scrollToFirstStep
          options={{
            overlayColor: 'rgba(0, 0, 0, 0.45)',
            overlayClickAction: false,
            zIndex: 1400,
            spotlightRadius: 12,
            buttons: ['back', 'close', 'primary', 'skip'],
          }}
        />
      )}
    </PageTourContext.Provider>
  )
}
